import React, { useState } from 'react';
import { Container, Form, Button, Card, ProgressBar, Alert, Tabs, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FolderPlus, FilePlus } from 'lucide-react';
import Navigation from '../components/Navbar';
import api from '../api';

const Upload = () => {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [genre, setGenre] = useState('');
    const [year, setYear] = useState('');
    const [folderFiles, setFolderFiles] = useState([]);
    
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [message, setMessage] = useState(null);
    const navigate = useNavigate();

    const CHUNK_SIZE = 10 * 1024 * 1024; // 10 Mo pour un bon compromis vitesse/stabilité

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setTitle(selectedFile.name.replace(/\.[^/.]+$/, ""));
        }
    };

    const handleFolderChange = (e) => {
        const files = Array.from(e.target.files);
        const videoFiles = files.filter(f => 
            f.type.startsWith('video/') || /\.(mp4|mkv|avi|mov)$/i.test(f.name)
        );
        setFolderFiles(videoFiles);
    };

    // --- FONCTION LOGIQUE D'ENVOI (CHUNKS + FINALISATION) ---
    const processFullUpload = async (fileToUpload, movieData) => {
        const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);
        const fileName = fileToUpload.name;

        // 1. Envoi des morceaux
        for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileToUpload.size);
            const chunk = fileToUpload.slice(start, end);

            const formData = new FormData();
            formData.append('chunk', chunk);
            formData.append('fileName', fileName);
            formData.append('chunkIndex', i);

            await api.post('/movies/upload-chunk', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Mise à jour de la barre de progression
            setProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        // 2. Finalisation (Assemblage + Base de données + Compression)
        await api.post('/movies/upload-complete', {
            fileName: fileName,
            totalChunks: totalChunks,
            title: movieData.title,
            description: movieData.description || '',
            genre: movieData.genre || 'Inconnu',
            release_year: movieData.year || ''
        });
    };

    const startUpload = async (e) => {
        e.preventDefault();
        setUploading(true);
        setMessage(null);
        setProgress(0);

        try {
            if (folderFiles.length > 0) {
                // Mode Dossier
                for (let i = 0; i < folderFiles.length; i++) {
                    setCurrentFileIndex(i + 1);
                    setProgress(0);
                    await processFullUpload(folderFiles[i], { 
                        title: folderFiles[i].name.replace(/\.[^/.]+$/, ""), 
                        genre: 'Inconnu' 
                    });
                }
                setMessage({ type: 'success', text: `${folderFiles.length} films ajoutés ! Compression lancée.` });
            } else {
                // Mode Unique
                setCurrentFileIndex(1);
                await processFullUpload(file, { title, description, genre, year });
                setMessage({ type: 'success', text: 'Film ajouté ! Le serveur l\'optimise actuellement.' });
            }
            setTimeout(() => navigate('/'), 2000);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'danger', text: 'Erreur lors de l\'envoi. Vérifiez votre connexion.' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="min-vh-100 pb-5" style={{ backgroundColor: '#050505', color: 'white' }}>
            <Navigation />
            <Container className="mt-5 pt-5" style={{ maxWidth: '750px' }}>
                <Card className="bg-dark text-white border-secondary shadow-lg overflow-hidden">
                    <div className="p-4 border-bottom border-secondary bg-black text-center">
                        <h3 className="fw-bold m-0 text-danger">Upload Anti-Coupure</h3>
                        <small className="text-muted text-uppercase">Transfert segmenté haute stabilité</small>
                    </div>

                    <Tabs defaultActiveKey="single" className="custom-tabs bg-dark border-0 px-3">
                        <Tab eventKey="single" title={<span><FilePlus size={18} /> Unique</span>} className="p-4">
                            <Form onSubmit={startUpload}>
                                <Form.Group className="mb-4">
                                    <Form.Label className="text-muted small fw-bold">FICHIER</Form.Label>
                                    <Form.Control type="file" onChange={handleFileChange} required={!uploading && folderFiles.length === 0} className="bg-dark border-secondary text-white" />
                                </Form.Group>
                                <Form.Group className="mb-4">
                                    <Form.Label className="text-muted small fw-bold">TITRE</Form.Label>
                                    <Form.Control type="text" value={title} onChange={e => setTitle(e.target.value)} required={folderFiles.length === 0} className="bg-dark border-secondary text-white" />
                                </Form.Group>
                                <div className="d-flex gap-3 mb-4">
                                    <Form.Control type="text" value={genre} onChange={e => setGenre(e.target.value)} placeholder="Genre" className="bg-dark border-secondary text-white" />
                                    <Form.Control type="number" value={year} onChange={e => setYear(e.target.value)} placeholder="Année" className="bg-dark border-secondary text-white" />
                                </div>
                                <Form.Group className="mb-4">
                                    <Form.Label className="text-muted small fw-bold">DESCRIPTION / SYNOPSIS</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={description} onChange={e => setDescription(e.target.value)} className="bg-dark border-secondary text-white" />
                                </Form.Group>
                                <Button variant="danger" type="submit" disabled={uploading || !file} className="w-100 fw-bold py-2 shadow-sm">
                                    {uploading ? 'Envoi du segment...' : 'Mettre en ligne'}
                                </Button>
                            </Form>
                        </Tab>

                        <Tab eventKey="folder" title={<span><FolderPlus size={18} /> Dossier</span>} className="p-4">
                            <div className="text-center">
                                <div className="upload-dropzone p-5 border border-dashed border-secondary rounded mb-3" style={{ position: 'relative', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                                    <FolderPlus size={48} className="text-danger mb-2" />
                                    <p className="mb-0">Cliquez pour choisir un dossier</p>
                                    <small className="text-muted">Toutes les vidéos seront importées individuellement</small>
                                    <input type="file" webkitdirectory="true" directory="true" multiple onChange={handleFolderChange} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                                </div>
                                {folderFiles.length > 0 && <Alert variant="info" className="bg-transparent text-info border-info">{folderFiles.length} vidéos détectées dans le dossier.</Alert>}
                                <Button variant="danger" onClick={startUpload} disabled={uploading || folderFiles.length === 0} className="w-100 fw-bold py-2">
                                    {uploading ? `Film ${currentFileIndex} sur ${folderFiles.length}` : `Lancer l'importation du dossier`}
                                </Button>
                            </div>
                        </Tab>
                    </Tabs>

                    {uploading && (
                        <div className="p-4 bg-black border-top border-secondary text-center">
                            <div className="d-flex justify-content-between mb-2">
                                <small className="text-muted">Progression du film {currentFileIndex}</small>
                                <small className="text-danger fw-bold">{progress}%</small>
                            </div>
                            <ProgressBar now={progress} variant="danger" animated className="mb-2" style={{ height: '10px' }} />
                            <small className="text-muted small">Ne fermez pas cette page pendant le transfert</small>
                        </div>
                    )}
                    {message && <Alert variant={message.type} className="m-3 bg-dark text-white border-secondary">{message.text}</Alert>}
                </Card>
            </Container>
            <style>{`
                .nav-tabs .nav-link { color: #888; border: none !important; transition: 0.3s; }
                .nav-tabs .nav-link:hover { color: #fff; }
                .nav-tabs .nav-link.active { background: none !important; color: #e50914 !important; border-bottom: 2px solid #e50914 !important; font-weight: bold; }
                .custom-tabs { margin-top: -1px; }
            `}</style>
        </div>
    );
};

export default Upload;